/**
 * 生成モデルのカタログ定数 (画像 / 動画 / シナリオ)。
 *
 * useGenerationState.tsx から切り出したもの。値・意味は一切変えていない。
 * useGenerationState からも再エクスポートしているため、既存の import は
 * どちらの経路でも動く。
 */

// ========================================
// 画像生成モデル設定
// ========================================
/** 利用可能な画像生成モデル
 * - Gemini (nano-banana): PNG のみ出力
 *   - 標準 = gemini-nano-banana-2.1 (2026-10-08 導入。3.1 Flash の後継・1K/2K/4K)
 *   - 旧標準 gemini-3.1-flash-image-preview (と 2.5 Flash) は同日に廃止。512px も廃止。
 *     旧値は IMAGE_MODEL_LEGACY_MAP で標準へ読み替える (docs/features/nano-banana-2.1-migration.md)
 * - OpenAI GPT Image 2.5 Flare (2026-09-08 導入・既定。通称「標準」): quality 別に 6 プラン (auto/max/xhigh/high/medium/low)
 * - OpenAI GPT Image 2.5 Sunburst (同日導入・通称「長時間」): auto/max/xhigh/high の 4 プラン
 *   (xhigh / max は 2026-09-09 に仮ポイント 120 / 200pt で解放)
 * - OpenAI GPT Image 2 (旧既定): 2026-09-09 に廃止。localStorage 等の旧値は IMAGE_MODEL_LEGACY_MAP で
 *   同品質の Flare へ読み替える
 *   - いずれもバックエンドで接尾辞を剥がし OpenAI API の model / quality パラメータに変換
 *     (docs/features/gpt-image-2.5-migration.md)
 */
export type GenerationModelType =
  | 'gemini-nano-banana-2.1'
  | 'gemini-3.1-flash-lite-image'
  | 'gemini-3-pro-image-preview'
  | 'gpt-image-2.5-flare-auto'
  | 'gpt-image-2.5-flare-max'
  | 'gpt-image-2.5-flare-xhigh'
  | 'gpt-image-2.5-flare-high'
  | 'gpt-image-2.5-flare-medium'
  | 'gpt-image-2.5-flare-low'
  | 'gpt-image-2.5-sunburst-auto'
  | 'gpt-image-2.5-sunburst-max'
  | 'gpt-image-2.5-sunburst-xhigh'
  | 'gpt-image-2.5-sunburst-high';

/**
 * 廃止した画像モデル model_code → 現行 model_code の読み替え表。
 * localStorage (静止画: settingsStorage / シナリオ: useScenarioFormState) に残った旧値を
 * 起動時に現行コードへ寄せる。バックエンド側にも同じ表
 * (constants.LEGACY_GPT_IMAGE_MODEL_ALIASES) があり二重防御 (再実行や保存済みシナリオの
 * generation_model は BE 側で読み替える)。
 * 2026-09-09: gpt-image-2-{q} → gpt-image-2.5-flare-{q} (同品質・同価格)。
 * 2026-10-08: gemini-3.1-flash-image(-preview) / gemini-2.5-flash-image → gemini-nano-banana-2.1 (標準)。
 */
export const IMAGE_MODEL_LEGACY_MAP: Readonly<Record<string, GenerationModelType>> = {
  'gpt-image-2-auto': 'gpt-image-2.5-flare-auto',
  'gpt-image-2-high': 'gpt-image-2.5-flare-high',
  'gpt-image-2-medium': 'gpt-image-2.5-flare-medium',
  'gpt-image-2-low': 'gpt-image-2.5-flare-low',
  'gemini-3.1-flash-image-preview': 'gemini-nano-banana-2.1',
  'gemini-3.1-flash-image': 'gemini-nano-banana-2.1',
  'gemini-2.5-flash-image': 'gemini-nano-banana-2.1',
};

/** 廃止済み model_code を現行へ読み替える (該当しなければそのまま返す)。 */
export function resolveLegacyImageModel<T extends string | null | undefined>(modelCode: T): T | GenerationModelType {
  if (!modelCode) return modelCode;
  return IMAGE_MODEL_LEGACY_MAP[modelCode] ?? modelCode;
}

/** デフォルトの画像生成モデル（LocalStorageに値がない場合に使用）。
 *  generation_models の is_default=TRUE 行と一致させる (2026-09-08: gpt-image-2-auto →
 *  gpt-image-2.5-flare-auto。migration 20260908_add_gpt_image_2_5.sql)。 */
export const DEFAULT_GENERATION_MODEL: GenerationModelType = 'gpt-image-2.5-flare-auto';

/** Gemini「標準」のモデル名 (定数名は歴史的経緯で SPEED のまま。UI 表示ラベルは「標準」)。
 *  2026-10-08: gemini-3.1-flash-image-preview → gemini-nano-banana-2.1 に切替 (旧コードは廃止)。
 *  思考レベル UI・image_size_speed キー・絵コンテ既定・シナリオ既定モデルはこの定数に追従する。 */
export const GENERATION_MODEL_SPEED: GenerationModelType = 'gemini-nano-banana-2.1';

/** 高品質モードのモデル名 */
export const GENERATION_MODEL_QUALITY: GenerationModelType = 'gemini-3-pro-image-preview';

/** ライト (最速・最安) モードのモデル名。1K 固定 (2K/4K 非対応・料率行も 1K のみ)。
 *  UI 表示ラベルは「スピード」(20260702_reposition_gemini_image_model_labels.sql)。 */
export const GENERATION_MODEL_LITE: GenerationModelType = 'gemini-3.1-flash-lite-image';

/** 思考レベル。Gemini 画像モデルの API 値 (minimal / medium / high) と同じ。
 *  2026-10-08: medium (2.1 の API 既定) を追加し、画面の既定も medium「標準」に。 */
export type ThinkingLevel = 'minimal' | 'medium' | 'high';
export const THINKING_LEVELS: readonly ThinkingLevel[] = ['minimal', 'medium', 'high'];
export const DEFAULT_THINKING_LEVEL: ThinkingLevel = 'medium';
/** 画面の選択肢 (表示順)。モデル別の絞り込みは getThinkingLevelOptions()。 */
export const THINKING_LEVEL_OPTIONS: readonly { key: ThinkingLevel; label: string }[] = [
  { key: 'minimal', label: '最小限' },
  { key: 'medium', label: '標準' },
  { key: 'high', label: '熟考' },
];

/** モデル別に受け付ける思考レベルと、その API 既定。
 *  出典: ai.google.dev/gemini-api/docs/image-generation#thinking-process (2026-10-08 取得)。
 *  backend/services/image_generation/constants.GEMINI_THINKING_LEVELS_BY_MODEL と同期。
 *    - 標準 (Nano Banana 2.1): minimal / medium / high (既定 medium)
 *    - スピード (3.1 Flash Lite): minimal / high (既定 minimal。medium 非対応)
 *    - 高品質 (3 Pro): thinking_level 非対応 */
export const THINKING_LEVELS_BY_MODEL: Readonly<Record<string, { levels: readonly ThinkingLevel[]; defaultLevel: ThinkingLevel }>> = {
  [GENERATION_MODEL_SPEED]: { levels: ['minimal', 'medium', 'high'], defaultLevel: 'medium' },
  [GENERATION_MODEL_LITE]: { levels: ['minimal', 'high'], defaultLevel: 'minimal' },
};

/** モデルが思考レベルの指定に対応するか (UI 表示・payload 付与の判定を 1 箇所に集約)。 */
export function supportsThinkingLevel(modelCode: string | null | undefined): boolean {
  return !!modelCode && modelCode in THINKING_LEVELS_BY_MODEL;
}

/** モデルの選択肢 (表示順)。非対応モデルは空。 */
export function getThinkingLevelOptions(modelCode: string | null | undefined): readonly { key: ThinkingLevel; label: string }[] {
  const def = modelCode ? THINKING_LEVELS_BY_MODEL[modelCode] : undefined;
  if (!def) return [];
  return THINKING_LEVEL_OPTIONS.filter((o) => def.levels.includes(o.key));
}

/** モデルの既定の思考レベル。非対応モデルは全体既定。 */
export function getDefaultThinkingLevel(modelCode: string | null | undefined): ThinkingLevel {
  return (modelCode && THINKING_LEVELS_BY_MODEL[modelCode]?.defaultLevel) || DEFAULT_THINKING_LEVEL;
}

/** 不明な値 (リセット経由の 'none' や旧値) は既定の medium に倒す。 */
export function normalizeThinkingLevel(value: unknown): ThinkingLevel {
  return (THINKING_LEVELS as readonly string[]).includes(value as string) ? (value as ThinkingLevel) : DEFAULT_THINKING_LEVEL;
}

/** 保存中の思考レベルをモデルに合わせて解決する。モデルが受け付けない値 (Lite への medium 等) は
 *  そのモデルの既定に倒す。画面の表示値と送信値の両方で使う。 */
export function resolveThinkingLevelForModel(modelCode: string | null | undefined, level: unknown): ThinkingLevel {
  const def = modelCode ? THINKING_LEVELS_BY_MODEL[modelCode] : undefined;
  const normalized = normalizeThinkingLevel(level);
  if (!def) return normalized;
  return def.levels.includes(normalized) ? normalized : def.defaultLevel;
}

/** 生成リクエストに載せる thinking_level。非対応モデルは undefined (送らない)。 */
export function thinkingLevelPayload(modelCode: string | null | undefined, level: unknown): ThinkingLevel | undefined {
  return supportsThinkingLevel(modelCode) ? resolveThinkingLevelForModel(modelCode, level) : undefined;
}

/** 思考レベルの localStorage キー。静止画スタジオとシナリオ作成フォームで共有する。
 *  旧キー 'cm_scenario_thinking_level' は毎回マウント時に state を書き戻していたため、一度も触って
 *  いないユーザーにも 'minimal' が残っている。medium を新しい既定にするため v2 キーへ移行し、
 *  旧値は明示選択の可能性が高い 'high' だけ引き継ぐ (それ以外は既定 medium から始める)。 */
export const STORAGE_KEY_THINKING_LEVEL = 'cm_scenario_thinking_level_v2';
const LEGACY_STORAGE_KEY_THINKING_LEVEL = 'cm_scenario_thinking_level';

export function readStoredThinkingLevel(): ThinkingLevel {
  try {
    const saved = localStorage.getItem(STORAGE_KEY_THINKING_LEVEL);
    if (saved !== null) return normalizeThinkingLevel(saved);
    const legacy = localStorage.getItem(LEGACY_STORAGE_KEY_THINKING_LEVEL);
    return legacy === 'high' ? 'high' : DEFAULT_THINKING_LEVEL;
  } catch {
    return DEFAULT_THINKING_LEVEL;
  }
}


// ========================================
// クレジット計算用リソースタイプ（DBの credit_rate_config.resource_type に対応）
// ========================================
/** 画像生成リソースタイプ（標準 = Nano Banana 2.1: 解像度別。512px は廃止済みで行も無い） */
export const RESOURCE_TYPE_IMAGE_SPEED = 'gemini-nano-banana-2.1';
export const RESOURCE_TYPE_IMAGE_SPEED_2K = 'gemini-nano-banana-2.1-2k';
export const RESOURCE_TYPE_IMAGE_SPEED_4K = 'gemini-nano-banana-2.1-4k';
/** 画像生成リソースタイプ（高品質モード: 解像度別） */
export const RESOURCE_TYPE_IMAGE_QUALITY_2K = 'gemini-3-pro-image-preview-2k';
export const RESOURCE_TYPE_IMAGE_QUALITY_4K = 'gemini-3-pro-image-preview-4k';
/** 画像生成リソースタイプ（GPT Image 2.5 Flare auto: 解像度別）
 *  backend/services/credit_service._resolve_image_resource_type の規約と同期:
 *  1K は無接尾、2K/4K は接尾辞付き、絵コンテ (1280px) は -storyboard。
 *  AI俳優生成 (actor-generation) と Express 絵コンテが固定で参照する
 *  (2026-09-09 に gpt-image-2-auto から切替)。 */
export const RESOURCE_TYPE_IMAGE_GPT25_FLARE_AUTO_1K = 'gpt-image-2.5-flare-auto';
export const RESOURCE_TYPE_IMAGE_GPT25_FLARE_AUTO_2K = 'gpt-image-2.5-flare-auto-2k';
export const RESOURCE_TYPE_IMAGE_GPT25_FLARE_AUTO_4K = 'gpt-image-2.5-flare-auto-4k';
export const RESOURCE_TYPE_IMAGE_GPT25_FLARE_AUTO_STORYBOARD = 'gpt-image-2.5-flare-auto-storyboard';
/** 画像生成リソースタイプ（ライトモード）。1K 固定モデルのため 2K/4K 行が存在せず、
 *  接尾辞なしの単一 resource_type のみ (20260701_add_gemini_3_1_flash_lite_image.sql)。 */
export const RESOURCE_TYPE_IMAGE_LITE = 'gemini-3.1-flash-lite-image';

// ========================================
// 動画生成モデル設定
// ========================================
// generation_models テーブルの model_code をそのまま受け入れる。
// 旧来の union 型 ('veo-3.1' | 'veo-3.1-fast' | 'veo-3.1-lite') は撤廃し、
// 新規モデル (Seedance / Kling 等) を追加する際にフロント定数を増やさずに済むよう
// string に緩める。文字列比較は引き続き有効。
export type VideoModelType = string;

export const VIDEO_MODEL_STANDARD: VideoModelType = 'veo-3.1';
export const VIDEO_MODEL_FAST: VideoModelType = 'veo-3.1-fast';
export const VIDEO_MODEL_LITE: VideoModelType = 'veo-3.1-lite';
// 初回ユーザー (localStorage 未設定) の既定動画モデル: Seedance 2.5 標準。
// generation_models の is_default=TRUE 行と一致させる (migration 20260814_01 で
// 2.0 標準 → 2.5 標準へ移した)。2.5 が partner 側で非表示の場合は
// VideoGenerationSettings の正規化が一覧内の is_default 行へフォールバックする。
export const DEFAULT_VIDEO_MODEL: VideoModelType = 'seedance-25-standard';

/** Veo モデル → Vertex AI のモデルID (バックエンドのレガシー資産用に残す) */
export const VIDEO_MODEL_IDS: Record<string, string> = {
  'veo-3.1': 'veo-3.1-generate-001',
  'veo-3.1-fast': 'veo-3.1-fast-generate-001',
  'veo-3.1-lite': 'veo-3.1-lite-generate-001',
};

/** 動画生成リソースタイプ（モデル×解像度×音声 の組み合わせ） */
export const RESOURCE_TYPE_VIDEO_WITH_AUDIO = 'veo-with-audio';
export const RESOURCE_TYPE_VIDEO_WITHOUT_AUDIO = 'veo-without-audio';

// モデル別リソースタイプ（admin の credit_rate_config に登録するキー）
// Standard: 音声あり/なし両対応
export const RESOURCE_TYPE_VIDEO_STANDARD_720_AUDIO = 'veo-3.1-720p-with-audio';
export const RESOURCE_TYPE_VIDEO_STANDARD_720_NO_AUDIO = 'veo-3.1-720p-without-audio';
export const RESOURCE_TYPE_VIDEO_STANDARD_1080_AUDIO = 'veo-3.1-1080p-with-audio';
export const RESOURCE_TYPE_VIDEO_STANDARD_1080_NO_AUDIO = 'veo-3.1-1080p-without-audio';
// Fast: 音声あり/なし両対応
export const RESOURCE_TYPE_VIDEO_FAST_720_AUDIO = 'veo-3.1-fast-720p-with-audio';
export const RESOURCE_TYPE_VIDEO_FAST_720_NO_AUDIO = 'veo-3.1-fast-720p-without-audio';
export const RESOURCE_TYPE_VIDEO_FAST_1080_AUDIO = 'veo-3.1-fast-1080p-with-audio';
export const RESOURCE_TYPE_VIDEO_FAST_1080_NO_AUDIO = 'veo-3.1-fast-1080p-without-audio';
// Lite: 音声あり/なし両対応
export const RESOURCE_TYPE_VIDEO_LITE_720_AUDIO = 'veo-3.1-lite-720p-with-audio';
export const RESOURCE_TYPE_VIDEO_LITE_720_NO_AUDIO = 'veo-3.1-lite-720p-without-audio';
export const RESOURCE_TYPE_VIDEO_LITE_1080_AUDIO = 'veo-3.1-lite-1080p-with-audio';
export const RESOURCE_TYPE_VIDEO_LITE_1080_NO_AUDIO = 'veo-3.1-lite-1080p-without-audio';

/** 音声有無で課金が変わらないファミリー (Seedance 2.0 系)。
 *  backend.services.credit.rates._is_audio_invariant_video_family と同期。
 */
export function isAudioInvariantVideoFamily(model: string | undefined | null): boolean {
  if (!model) return false;
  const m = model.toLowerCase();
  // Gemini Omni Flash も音声をネイティブ生成し追加コストなし (backend と同期)
  // MiniMax H3 はネイティブステレオ音声を常に生成し ON/OFF パラメータ自体が無いため同様に扱う。
  return (
    m.startsWith('seedance')
    || m.startsWith('dreamina-seedance')
    || m.startsWith('gemini-omni')
    || m.startsWith('omni')
    || m.startsWith('minimax')
  );
}

/** 解像度ラベルを表示・resource_type 生成用に整形する。
 *  '720'/'1080' のようなピクセル解像度は末尾に 'p' を付ける ('720p')。
 *  '2K'/'4K' のような 'k' 単位表記 (MiniMax 等) はピクセル解像度と系統が異なり、
 *  'p' を付けると 'minimax-h3-2kp' のような不自然な表記になるため付けない。
 *  backend.services.credit.rates._resolve_video_resource_type の 'k' 判定と同期。
 */
export function formatVideoResolutionLabel(resolution: string): string {
  if (!resolution) return resolution;
  const lower = resolution.toLowerCase();
  if (lower.endsWith('k') || lower.endsWith('p')) return resolution;
  return `${resolution}p`;
}

/** 解像度の降順ソート用の比較値。ピクセル解像度 ('720'/'1080') はそのまま数値化し、
 *  'K' 単位表記 ('2K'/'4K') はピクセル解像度と同じ尺度に載せるため ×1000 して比較する
 *  (2K=2000 は 1080p より上位、4K=4000 はさらに上位に来る)。
 *  Number() が NaN になる値は 0 として扱い、既存の並び (末尾) を崩さない。
 */
export function videoResolutionSortValue(resolution: string): number {
  const lower = (resolution || '').toLowerCase();
  if (lower.endsWith('k')) {
    const n = Number.parseFloat(lower);
    return Number.isFinite(n) ? n * 1000 : 0;
  }
  const n = Number(resolution);
  return Number.isFinite(n) ? n : 0;
}

/** モデル×解像度×音声 → resource_type を解決する。
 *  resolution は数値文字列 ('480'/'720'/'1080') を受け取り、末尾 'p' を付ける。
 *  '720p' のように既に 'p' が付いている場合はそのまま使う。
 *  '2K'/'4K' のような 'k' 単位表記 (MiniMax 等、provider.normalize_resolution() 由来で
 *  大文字混じりのことがある) は小文字化のみ行い 'p' を付けない (backend と同期)。
 *  Seedance / MiniMax 系は音声非依存のため audio 接尾辞を付けない (backend と同期)。
 *  video-to-video (参照動画入力) は末尾に '-v2v' を付けた別レートキー
 *  (backend.services.credit.rates._resolve_video_resource_type と同期)。
 */
export function resolveVideoResourceType(
  model: string,
  resolution: string,
  generateAudio: boolean,
  hasReferenceVideo = false,
): string {
  const resLower = (resolution || '1080').toLowerCase().replace(/p$/, '');
  const resPart = resLower.endsWith('k') ? resLower : `${resLower}p`;
  const v2vSuffix = hasReferenceVideo ? '-v2v' : '';
  if (isAudioInvariantVideoFamily(model)) {
    return `${model}-${resPart}${v2vSuffix}`;
  }
  return `${model}-${resPart}-${generateAudio ? 'with' : 'without'}-audio${v2vSuffix}`;
}

/** ナレーション生成リソースタイプ */
export const RESOURCE_TYPE_NARRATION = 'elevenlabs-tts';

// ========================================
// シナリオ生成モデル設定
// ========================================
// 注: claude-opus-4-7 / 4-8 / 5 は別件の Claude 移行で並存 (旧 2 つは再実行互換の温存)。今回の
// Gemini ラインナップ刷新 (docs/features/gemini-lineup-refresh-2026q2.md) では触らない。
export type ScenarioModelType =
  | 'gemini-3.5-flash-lite'          // エントリー枠 (2026-07: 3.1 Flash-Lite → 3.5 Flash-Lite GA に差し替え)
  | 'gemini-3.8-flash'               // 中間枠 (3.5→3.6→3.7 Flash → 2026-09: 3.8 Flash に差し替え)
  | 'gemini-3.1-pro'                 // Phase 1: GA 版 (旧 gemini-3.1-pro-preview / gemini-2.5-pro を置換)
  | 'gemini-4-argon'                 // Gemini 高品質枠 (2026-10 新設: Gemini 4 Argon。3.1 Pro と併存)
  | 'gpt-6-luna'                     // OpenAI スピード枠 (2026-09: 5.6 Luna → 6 Luna に差し替え)
  | 'gpt-6-sol'
  | 'gpt-6.1-sol'                    // OpenAI 標準枠 (2026-09: 5.6 Terra / 5.6 Sol → 6 Sol に統合 → 2026-09-29: 6.1 Sol に差し替え)
  | 'gpt-6-astra'                    // OpenAI 高品質枠 (2026-09 新設: GPT-6 Astra)
  | 'claude-sonnet-5'
  | 'claude-sonnet-5-5'              // Claude スピード枠 (2026-09: Sonnet 4.6 → 5 → 2026-09-29: 5.5 に差し替え)
  | 'claude-opus-4-7'
  | 'claude-opus-4-8'
  | 'claude-opus-5'
  | 'claude-opus-5-5'                // Claude 標準枠 (2026-09: Opus 5 → 5.5 に差し替え)
  | 'claude-fable-5'
  | 'claude-fable-5-1';               // Claude 高品質枠 (2026-09: Fable 5 → 5.1 に差し替え)
// SCENARIO_MODEL_FLASH は定数名据置で「中身だけ差し替え」する方針
// (2.5 Flash → 3.1 Flash-Lite → 2026-07: 3.5 Flash-Lite)。
export const SCENARIO_MODEL_FLASH: ScenarioModelType = 'gemini-3.5-flash-lite';
export const SCENARIO_MODEL_FLASH_MID: ScenarioModelType = 'gemini-3.8-flash';  // 中間枠 (2026-09: 中身を 3.8 Flash に差し替え)
// Phase 1: SCENARIO_MODEL_PRO の意味を 2.5-pro → 3.1-pro GA に再割り当て
// (旧 SCENARIO_MODEL_PREVIEW は廃止し PRO に統合)。
export const SCENARIO_MODEL_PRO: ScenarioModelType = 'gemini-3.1-pro';
// 2026-10-14 新設: Google 新フラッグシップ (docs/features/gemini-4-argon-migration.md)。3.1 Pro と併存 (alias 無し)。
export const SCENARIO_MODEL_ARGON: ScenarioModelType = 'gemini-4-argon';
// OpenAI 枠は 2026-09-22 に GPT-6 世代へ差し替え (docs/features/gpt-6-sol-luna-migration.md)。
// GPT-6 に Terra は無く、6 Sol が 5.6 Terra より安いため、5.6 の 3 段 (Sol / Terra / Luna) を
// 高品質=Astra / 標準=6 Sol / スピード=6 Luna の 3 段に組み替えた。旧定数 SCENARIO_MODEL_GPT54 /
// GPT54_PRO / GPT56_LUNA は (5.4 世代の名前のまま 5.6 を指していて紛らわしかったため) ここで廃止。
// 定数名は据置で中身のみ差し替え (2026-09-29: 6 Sol → 6.1 Sol。docs/features/gpt-6.1-sol-migration.md)
export const SCENARIO_MODEL_GPT6_SOL: ScenarioModelType = 'gpt-6.1-sol';
export const SCENARIO_MODEL_GPT6_LUNA: ScenarioModelType = 'gpt-6-luna';
// 2026-09-05 新設: OpenAI 新フラッグシップ (docs/features/gpt-6-astra-migration.md)。
export const SCENARIO_MODEL_GPT6_ASTRA: ScenarioModelType = 'gpt-6-astra';
// 定数名は据置で中身のみ差し替え (2026-09: Sonnet 4.6 → 5 → 2026-09-29: 5.5。docs/features/claude-sonnet-5.5-migration.md)
export const SCENARIO_MODEL_CLAUDE_SONNET: ScenarioModelType = 'claude-sonnet-5-5';
// 「標準」枠。中身は 4.7 → 4.8 → 5 → 5.5 と差し替えてきており、定数名は据え置く
// (2026-09: Opus 5 → 5.5。docs/features/claude-opus-5.5-migration.md)。
export const SCENARIO_MODEL_CLAUDE_OPUS: ScenarioModelType = 'claude-opus-5-5';
// Opus の上位ティア。「高品質」枠として Opus 5.5 (標準) と併存させる。
// 定数名は据置で中身のみ差し替え (2026-09: Fable 5 → 5.1。docs/features/claude-fable-5.1-migration.md)
export const SCENARIO_MODEL_CLAUDE_FABLE: ScenarioModelType = 'claude-fable-5-1';
// 「ランダム」枠。実モデルではなく、有効な全モデルから 1 本ずつ生成する特別値
// (上限は指定シナリオ本数)。バックエンド scenario_generator.RANDOM_SCENARIO_MODEL と一致させる。
export const SCENARIO_MODEL_RANDOM = 'random';
// 初回ユーザー (localStorage 未設定) のデフォルトモデル。全モデルから1案ずつ作る「ランダム」枠を既定にする。
export const DEFAULT_SCENARIO_MODEL: string = SCENARIO_MODEL_RANDOM;

/** シナリオ生成リソースタイプ（credit_rate_config.resource_type に対応） */
export const RESOURCE_TYPE_SCENARIO_FLASH = 'gemini-3.5-flash-lite-scenario-generation';  // 2026-07: 3.5 Flash-Lite に再割り当て
export const RESOURCE_TYPE_SCENARIO_FLASH_MID = 'gemini-3.8-flash-scenario-generation';  // 2026-09: 3.8 Flash に差し替え
export const RESOURCE_TYPE_SCENARIO_PRO = 'gemini-3.1-pro-scenario-generation';  // Phase 1: 2.5-pro → 3.1-pro GA に再割り当て
export const RESOURCE_TYPE_SCENARIO_ARGON = 'gemini-4-argon-scenario-generation';  // 2026-10 新設
export const RESOURCE_TYPE_SCENARIO_GPT6_SOL = 'gpt-6.1-sol-scenario-generation';  // 2026-09: 5.6 Terra / Sol → 6 Sol → 2026-09-29: 6.1 Sol
export const RESOURCE_TYPE_SCENARIO_GPT6_LUNA = 'gpt-6-luna-scenario-generation';  // 2026-09: 5.6 Luna → 6 Luna
export const RESOURCE_TYPE_SCENARIO_GPT6_ASTRA = 'gpt-6-astra-scenario-generation';  // 2026-09 新設
export const RESOURCE_TYPE_SCENARIO_CLAUDE_SONNET = 'claude-sonnet-5-5-scenario-generation';  // 2026-09-29: Sonnet 5.5 に差し替え
export const RESOURCE_TYPE_SCENARIO_CLAUDE_OPUS = 'claude-opus-5-5-scenario-generation';  // 2026-09: Opus 5.5 に差し替え
export const RESOURCE_TYPE_SCENARIO_CLAUDE_FABLE = 'claude-fable-5-1-scenario-generation';  // 2026-09: Fable 5.1 に差し替え

/**
 * 旧 model id → 新 model id への localStorage マイグレーションマップ。
 * docs/features/gemini-lineup-refresh-2026q2.md Phase 1 で導入。
 * 起動時に localStorage の scenarioModel が旧 id を保持していた場合、新 id に
 * 書き換える。バックエンド側にも LEGACY_MODEL_ALIASES があるので二重防御。
 * Phase 4 で alias 撤去と同時に削除する。
 */
export const SCENARIO_MODEL_LEGACY_MAP: Record<string, ScenarioModelType> = {
  'gemini-3.1-pro-preview': 'gemini-3.1-pro',          // Phase 1
  'gemini-2.5-pro': 'gemini-3.1-pro',                  // Phase 1 (UI 露出なしだが念のため)
  // マップは 1 段 lookup でチェーンしないため、多段の旧 id は最終形へ直接張る。
  'gemini-2.5-flash': 'gemini-3.5-flash-lite',            // Phase 3 で 3.1-flash-lite → 2026-07 に最終形へ更新
  'gemini-3.1-flash-lite-preview': 'gemini-3.5-flash-lite', // 2026-07: エントリー枠を 3.5 Flash-Lite に
  'gemini-3.5-flash': 'gemini-3.8-flash',                 // 2026-07 に 3.6 → 2026-08 に 3.7 → 2026-09 に最終形 3.8 Flash へ更新
  'gemini-3.6-flash': 'gemini-3.8-flash',                 // 2026-08 に 3.7 Flash → 2026-09 に最終形 3.8 Flash へ更新
  'gemini-3.7-flash': 'gemini-3.8-flash',                 // 2026-09: 中間枠を 3.8 Flash に
  'gpt-5.4': 'gpt-6.1-sol',                               // 2026-08 に 5.6 Terra → 2026-09 に 6 Sol → 2026-09-29 に最終形 6.1 Sol へ更新
  'gpt-5.4-pro': 'gpt-6.1-sol',                           // 2026-08 に 5.6 Sol → 2026-09 に 6 Sol → 2026-09-29 に最終形 6.1 Sol へ更新
  'gpt-5.6-sol': 'gpt-6.1-sol',                           // 2026-09: OpenAI 高品質枠 (20pt) → 標準枠 6 Sol (10pt) → 2026-09-29: 6.1 Sol
  'gpt-5.6-terra': 'gpt-6.1-sol',                         // 2026-09: OpenAI 標準枠を 6 Sol に (10pt 据置) → 2026-09-29: 6.1 Sol
  'gpt-5.6-luna': 'gpt-6-luna',                           // 2026-09: OpenAI スピード枠を 6 Luna に (2pt 据置)
  'claude-fable-5': 'claude-fable-5-1',                   // 2026-09: Claude 高品質枠を Fable 5.1 に (30pt 据置)
  'claude-sonnet-4-6': 'claude-sonnet-5-5',               // 2026-09 に Sonnet 5 → 2026-09-29 に最終形 Sonnet 5.5 へ更新
  'claude-opus-5': 'claude-opus-5-5',                     // 2026-09: Claude 標準枠を Opus 5.5 に (20pt 据置)
  'claude-sonnet-5': 'claude-sonnet-5-5',                 // 2026-09-29: Claude スピード枠を Sonnet 5.5 に (10pt 据置)
  'gpt-6-sol': 'gpt-6.1-sol',                             // 2026-09-29: OpenAI 標準枠を 6.1 Sol に (10pt 据置)
};
